import { useCallback, useEffect, useRef, useState } from "react";
import { requestTimeout } from "../lib/requestTimeout";

/** A bounded read with Retry, no cross-site data, and the last value kept during a refresh. */
export function useResource<T>(load: (signal: AbortSignal) => Promise<T>, key: string, enabled = true) {
  const [state, setState] = useState<{ key: string; data?: T; busy: boolean; error: string | null }>({ key, busy: enabled, error: null });
  const active = useRef<AbortController | null>(null);
  const reload = useCallback(async (): Promise<T | undefined> => {
    active.current?.abort();
    const controller = new AbortController();
    active.current = controller;
    if (!enabled) { setState({ key, busy: false, error: null }); return; }
    setState(before => ({ key, data: before.key === key ? before.data : undefined, busy: true, error: null }));
    try {
      const data = await requestTimeout(load, controller.signal);
      if (!controller.signal.aborted) { setState({ key, data, busy: false, error: null }); return data; }
    } catch (error) {
      if (!controller.signal.aborted) setState(before => ({ ...before, busy: false, error: error instanceof Error ? error.message : "This couldn't be loaded. Try again." }));
    }
  }, [load, key, enabled]);
  const clear = useCallback(() => { active.current?.abort(); setState({ key, busy: false, error: null }); }, [key]);
  /** A completed owner write supersedes an older index read. */
  const replace = useCallback((data: T) => { active.current?.abort(); setState({ key, data, busy: false, error: null }); }, [key]);
  useEffect(() => { void reload(); return () => active.current?.abort(); }, [reload]);
  return { data: enabled && state.key === key ? state.data : undefined, busy: enabled && (state.key !== key || state.busy), error: enabled && state.key === key ? state.error : null, reload, clear, replace };
}
